import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CreditCard, Plus } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const CARD_TYPE_LABELS = { carburante: 'Carburante', telepass: 'Telepass', prepagata: 'Prepagata' };

function PaymentCardSection({ card }) {
  const [expenses, setExpenses] = useState([]);
  const [loadingExp, setLoadingExp] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', location: '', reason: '', used_by: '', receipt_url: '' });
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadExpenses = async () => {
    setLoadingExp(true);
    try {
      const r = await api.get(`/payment-cards/${card.id}/expenses`);
      setExpenses(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingExp(false); }
  };

  useEffect(() => { loadExpenses(); /* eslint-disable-next-line */ }, [card.id]);

  const openNew = () => {
    setForm({ date: new Date().toISOString().split('T')[0], amount: '', location: '', reason: '', used_by: '', receipt_url: '' });
    setShowDialog(true);
  };

  const handleReceiptUpload = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/payment-cards/${card.id}/expenses/scan-receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({
        ...f,
        date: r.data.date || f.date,
        amount: r.data.amount != null ? String(r.data.amount) : f.amount,
        location: r.data.location || f.location,
        reason: r.data.reason || f.reason,
        receipt_url: r.data.receipt_url || '',
      }));
      if (!r.data.ai_available) alert("Scontrino caricato, ma la lettura automatica non e' disponibile: compila i campi a mano.");
    } catch (e) {
      alert('Errore lettura scontrino, compila i campi a mano');
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.date || !form.amount || !form.reason.trim()) {
      alert('Data, importo e motivo sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/payment-cards/${card.id}/expenses`, { ...form, amount: parseFloat(form.amount) });
      setShowDialog(false);
      loadExpenses();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore salvataggio spesa');
    } finally {
      setSaving(false);
    }
  };

  const totalSpent = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`my-card-${card.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-800">{CARD_TYPE_LABELS[card.type] || card.type} - {card.provider}</p>
            <p className="text-xs text-slate-500">{card.card_number ? `N. ${card.card_number}` : ''}{card.assigned_name ? ` · ${card.assigned_name}` : ''}</p>
          </div>
          <div className="text-right">
            {card.plafond != null && <p className="text-xs text-slate-500">Plafond: {Number(card.plafond).toFixed(2)} EUR</p>}
            <p className="text-xs text-slate-500">Speso registrato: {totalSpent.toFixed(2)} EUR</p>
          </div>
        </div>

        <Button size="sm" onClick={openNew} data-testid={`new-expense-${card.id}`}><Plus size={14} className="mr-1" /> Registra spesa</Button>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Importo</TableHead><TableHead>Dove</TableHead><TableHead>Motivo</TableHead><TableHead>Chi</TableHead><TableHead>Scontrino</TableHead></TableRow></TableHeader>
            <TableBody>
              {loadingExp ? (
                <TableRow><TableCell colSpan={6} className="text-center py-3 text-slate-400 text-sm">Caricamento...</TableCell></TableRow>
              ) : expenses.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-3 text-slate-400 text-sm">Nessuna spesa registrata</TableCell></TableRow>
              ) : expenses.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell className="text-sm">{formatDate(e.date)}</TableCell>
                  <TableCell className="text-sm">{Number(e.amount).toFixed(2)} EUR</TableCell>
                  <TableCell className="text-sm text-slate-500">{e.location || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-500">{e.reason}</TableCell>
                  <TableCell className="text-sm text-slate-500">{e.used_by || '-'}</TableCell>
                  <TableCell>{e.receipt_url ? <a href={`${process.env.REACT_APP_BACKEND_URL}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs underline">Vedi</a> : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Registra spesa</DialogTitle><DialogDescription>Carica lo scontrino per compilare automaticamente, oppure inserisci i dati a mano.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Scontrino (foto/PDF)</Label>
              <Input type="file" accept="image/*,.pdf" onChange={(e) => handleReceiptUpload(e.target.files?.[0])} disabled={scanning} data-testid={`receipt-upload-${card.id}`} />
              {scanning && <p className="text-xs text-slate-400 mt-1">Lettura scontrino in corso...</p>}
              {form.receipt_url && !scanning && <p className="text-xs text-green-600 mt-1">Scontrino caricato, campi precompilati - controlla prima di salvare.</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0"><Label className="text-xs">Data *</Label><Input type="date" className="w-full" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid={`expense-date-${card.id}`} /></div>
              <div className="min-w-0"><Label className="text-xs">Importo (EUR) *</Label><Input type="number" step="0.01" min="0" className="w-full" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid={`expense-amount-${card.id}`} /></div>
            </div>
            <div><Label className="text-xs">Dove</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Es. distributore, citta'..." data-testid={`expense-location-${card.id}`} /></div>
            <div><Label className="text-xs">Motivo *</Label><Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Es. Rifornimento carburante" data-testid={`expense-reason-${card.id}`} /></div>
            <div><Label className="text-xs">Chi ha usato la carta</Label><Input value={form.used_by} onChange={(e) => setForm((f) => ({ ...f, used_by: e.target.value }))} placeholder="Nome (se diverso da te)" data-testid={`expense-usedby-${card.id}`} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSubmit} disabled={saving || scanning} data-testid={`expense-submit-${card.id}`}>{saving ? 'Salvataggio...' : 'Salva spesa'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function MyCardsPage() {
  const { user } = useAuth();
  const [myCards, setMyCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get('/payment-cards/my');
      setMyCards(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-cards-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><CreditCard size={22} className="text-[#1a4b8c]" /> Le Mie Carte</h1>
        <p className="text-sm text-slate-500 mt-1">Carte carburante, Telepass e prepagate di cui sei responsabile: registra qui le spese da giustificare.</p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Caricamento...</div>
      ) : !user?.employee_id ? (
        <Card className="border-slate-200 shadow-sm"><CardContent className="text-center py-10 text-slate-400 text-sm">Il tuo account non e' collegato a una scheda dipendente.</CardContent></Card>
      ) : myCards.length === 0 ? (
        <Card className="border-slate-200 shadow-sm"><CardContent className="text-center py-10 text-slate-400 text-sm">Non sei responsabile di nessuna carta al momento.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {myCards.map((card) => (
            <PaymentCardSection key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
}
